import { RefObject, useEffect, useLayoutEffect } from 'react';

import {
  blurActiveElement,
  cycleArrayIndex,
  getCurrentFocusedIndex,
  getFocusableElements,
} from './useCatchFocusInElement.utils';

export function useCatchFocusInElement(ref: RefObject<HTMLElement | null>): void {
  let elements: HTMLElement[] = [];
  let currentFocusedIndex = 0;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const elements = getFocusableElements(ref.current);
        currentFocusedIndex = getCurrentFocusedIndex(elements);
      }
    });
  });

  useLayoutEffect(() => {
    blurActiveElement(ref); // in some cases we need to force active element blur
  }, []);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    observer.observe(ref.current, { subtree: true, childList: true });
    elements = getFocusableElements(ref.current);
    currentFocusedIndex = getCurrentFocusedIndex(elements);

    elements[currentFocusedIndex]?.focus();

    const catchFocus = (e: KeyboardEvent): void => {
      const isTabKey = e.key === 'Tab' || e.code === 'Tab';
      if (!isTabKey || !elements.length) {
        return;
      }
      currentFocusedIndex = cycleArrayIndex(currentFocusedIndex, elements.length, e.shiftKey);
      elements[currentFocusedIndex]?.focus();
      e.preventDefault();
    };

    const setCurrentFocusedIndex = (): void => {
      currentFocusedIndex = getCurrentFocusedIndex(elements);
    };

    document.addEventListener('focus', setCurrentFocusedIndex, true);
    document.addEventListener('keydown', catchFocus);

    return () => {
      document.removeEventListener('focus', setCurrentFocusedIndex, true);
      document.removeEventListener('keydown', catchFocus);
      observer.disconnect();
    };
  }, []);
}
